import { compact } from 'lodash';
import ReduxThunk from 'redux-thunk';
import { createLogger } from 'redux-logger';
import { connectRouter, routerMiddleware, LOCATION_CHANGE } from 'connected-react-router';
import { createHashHistory } from 'history';
import { configureStore, combineReducers, Middleware, StoreEnhancer } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { trackPageView } from '@src/metrics';
import { RESET_STORE } from '@src/redux/actions/main';
import * as reducers from './reducers';
import { AppDispatch, RootState } from './interfaces/redux.interface';

export const history = createHashHistory();
const isProduction = process.env.NODE_ENV === 'production';

const trackPageChange: Middleware = () => {
  return next => action => {
    const result = next(action);

    if (action.type === LOCATION_CHANGE) {
      trackPageView({ pagePath: action.payload.location.pathname });
    }

    return result;
  };
};

const logger: Middleware = createLogger({
  collapsed: true,
  duration: true,
});

const sentryReduxEnhancer: StoreEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: state => {
    // Transform the state to remove sensitive information
    const transformedState = {
      ...state,
      accessToken: '[hidden]',
    };

    return transformedState;
  },
});

const middleware: Middleware[] = compact([
  trackPageChange,
  routerMiddleware(history),
  ReduxThunk,
  !isProduction && logger,
]);

const enhancers: StoreEnhancer[] = compact([
  sentryReduxEnhancer,
  window.devToolsExtension && window.devToolsExtension(),
]);

const rootReducer = combineReducers({
  ...reducers,
  router: connectRouter(history),
});

export const useAppDispatch = (): AppDispatch => {
  return useDispatch<AppDispatch>();
};

const appReducer: Reducer<RootState, AnyAction> = (state, action) => {
  if (action.type === RESET_STORE) {
    return rootReducer(undefined, action);
  }

  return rootReducer(state, action);
};

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default configureStore({
  enhancers: enhancers,
  middleware: middleware,
  reducer: appReducer,
});
